import React, { useState, useEffect } from 'react';
import { Container, Grid, Paper, Typography, Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import ClientSelector from '../components/dashboard/ClientSelector';
import MetricsOverview from '../components/dashboard/MetricsOverview';
import IntegrationStatus from '../components/dashboard/IntegrationStatus';
import CampaignPerformance from '../components/dashboard/CampaignPerformance';
import DateRangeSelector from '../components/common/DateRangeSelector';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });
  const navigate = useNavigate();

  // Fetch clients on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await axios.get('/api/clients', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status === 'success') {
          setClients(response.data.data.clients);
          
          // Select first client by default if available
          if (response.data.data.clients.length > 0) {
            setSelectedClient(response.data.data.clients[0]);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setLoading(false);
        
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      }
    };
    
    fetchClients();
  }, [navigate]);

  // Fetch client metrics when client or date range changes
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!selectedClient) return;
      
      setLoading(true);
      
      try {
        const token = localStorage.getItem('token');
        const startDate = dateRange.startDate.toISOString().split('T')[0];
        const endDate = dateRange.endDate.toISOString().split('T')[0];
        
        const response = await axios.get(`/api/clients/${selectedClient._id}/metrics/analysis`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate,
            endDate
          }
        });
        
        if (response.data.status === 'success') {
          setMetrics(response.data.data.analysis);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setLoading(false);
      }
    };
    
    if (selectedClient) {
      fetchMetrics();
    }
  }, [selectedClient, dateRange]);

  const handleClientChange = (client) => {
    setSelectedClient(client);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
      minHeight: '100vh',
      pb: 4,
    }}>
      <Container maxWidth="xl" sx={{ pt: 4, pb: 4 }}>
        <Grid container spacing={3}>
          {/* Client and Date Selectors */}
          <Grid item xs={12}>
            <Paper 
              sx={{ 
                p: 3, 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                borderRadius: 3,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              <ClientSelector 
                clients={clients} 
                selectedClient={selectedClient} 
                onClientChange={handleClientChange} 
              />
              <Box sx={{ mt: { xs: 2, sm: 0 } }}>
                <DateRangeSelector 
                  dateRange={dateRange} 
                  onDateRangeChange={handleDateRangeChange} 
                />
              </Box>
            </Paper>
          </Grid>

        {loading ? (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Grid>
        ) : !selectedClient ? (
          <Grid item xs={12}>
            <Paper 
              sx={{ 
                p: 6, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                borderRadius: 3,
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 2,
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {clients.length > 0 
                  ? 'Selecione um cliente para visualizar os dados' 
                  : 'Nenhum cliente encontrado. Adicione seu primeiro cliente para começar.'}
              </Typography>
              {clients.length === 0 && (
                <Box sx={{ mt: 3 }}>
                  <button 
                    onClick={() => navigate('/clients/new')}
                    style={{
                      padding: '12px 32px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '1rem',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
                    }}
                  >
                    Adicionar Cliente
                  </button>
                </Box>
              )}
            </Paper>
          </Grid>
        ) : (
          <>
            {/* Metrics Overview */}
            <Grid item xs={12}>
              <MetricsOverview metrics={metrics} />
            </Grid>
            
            {/* Integration Status */}
            <Grid item xs={12} md={6}>
              <IntegrationStatus clientId={selectedClient._id} />
            </Grid>
            
            {/* Campaign Performance */}
            <Grid item xs={12} md={6}>
              <CampaignPerformance 
                clientId={selectedClient._id} 
                dateRange={dateRange} 
              />
            </Grid>
          </>
        )}
      </Grid>
    </Container>
    </Box>
  );
};

export default Dashboard;
